import { useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  preparation: { label: "En préparation", color: "bg-gray-500" },
  soumis:      { label: "Soumis à la Charity Commission", color: "bg-blue-600" },
  en_cours:    { label: "En cours d'examen", color: "bg-amber-600" },
  approuve:    { label: "Approuvé — enregistré", color: "bg-emerald-600" },
  rejete:      { label: "Rejeté — à corriger", color: "bg-red-600" },
};

/**
 * VerifyPage — Page publique lue depuis les QR-codes des PDF officiels SAFUAYE-TOGO.
 * URL : /verify?doc=SAFUAYE_TOGO_05_Lettre_Declaration
 */
export default function VerifyPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const doc = params.get("doc") || "";
    if (!doc) { setError("Aucun document précisé (paramètre ?doc=… manquant)"); setLoading(false); return; }
    fetch(`${API}/verify?doc=${encodeURIComponent(doc)}`)
      .then(r => r.json())
      .then(d => {
        if (!d.valid) setError(d.error || "Document introuvable");
        else setData(d);
        setLoading(false);
      })
      .catch(e => { setError("Erreur réseau : " + e.message); setLoading(false); });
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="text-gray-500 text-sm">Vérification du document…</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6" data-testid="verify-error">
      <div className="bg-white border border-red-300 rounded-xl p-8 max-w-lg text-center shadow">
        <div className="text-5xl mb-3">❌</div>
        <h1 className="font-heading text-xl font-bold text-red-700 mb-2">Document non vérifiable</h1>
        <p className="text-sm text-gray-600">{error}</p>
        <a href="/" className="inline-block mt-4 text-xs bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded font-semibold">Retour au site</a>
      </div>
    </div>
  );

  const s = data.official_status || {};
  const cs = STATUS_LABELS[s.charity_uk?.status] || STATUS_LABELS.preparation;
  const modified = new Date(data.modified_at);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50 py-8 px-3" data-testid="verify-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-emerald-700 text-white rounded-t-xl p-5 shadow">
          <div className="flex items-center gap-3">
            <div className="text-3xl">✅</div>
            <div>
              <div className="text-[11px] uppercase tracking-widest opacity-80">Vérification officielle</div>
              <h1 className="text-xl font-bold">Document SAFUAYE-TOGO authentique</h1>
            </div>
          </div>
        </div>

        {/* Doc info */}
        <div className="bg-white border-x border-b border-gray-200 rounded-b-xl p-5 shadow">
          <div className="flex items-start gap-3 mb-4">
            <div className="text-4xl">{data.meta.icon}</div>
            <div className="flex-1">
              <div className="font-heading text-lg font-bold text-gray-900" data-testid="verify-doc-title">{data.meta.title}</div>
              <div className="text-xs text-gray-500">Section : {data.meta.section} · Identifiant : {data.key}</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3 text-xs">
            <div className="bg-gray-50 rounded p-3">
              <div className="text-gray-500 uppercase tracking-wider text-[10px]">Empreinte SHA-256 (12 caractères)</div>
              <div className="font-mono font-bold text-gray-800 mt-1" data-testid="verify-hash">{data.hash}</div>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <div className="text-gray-500 uppercase tracking-wider text-[10px]">Dernière mise à jour</div>
              <div className="font-semibold text-gray-800 mt-1">{modified.toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</div>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <div className="text-gray-500 uppercase tracking-wider text-[10px]">Taille</div>
              <div className="font-semibold text-gray-800 mt-1">{data.size_kb} KB</div>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <div className="text-gray-500 uppercase tracking-wider text-[10px]">Loi applicable</div>
              <div className="font-semibold text-gray-800 mt-1">{data.association.loi}</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <a href={data.pdf_url} target="_blank" rel="noreferrer" className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded font-semibold" data-testid="verify-pdf">
              📄 Ouvrir le PDF officiel
            </a>
            {data.docx_url && (
              <a href={data.docx_url} target="_blank" rel="noreferrer" className="text-xs bg-sky-600 hover:bg-sky-700 text-white px-3 py-2 rounded font-semibold">
                📝 DOCX
              </a>
            )}
          </div>
        </div>

        {/* Association */}
        <div className="mt-5 bg-white border border-gray-200 rounded-xl p-5 shadow">
          <h2 className="font-heading text-sm font-bold text-gray-900 mb-3">🏛️ Association émettrice</h2>
          <div className="text-xs text-gray-700 space-y-1">
            <div><b>Nom :</b> {data.association.name}</div>
            <div><b>Siège :</b> {data.association.siege}</div>
            <div><b>Président :</b> {data.association.president}</div>
          </div>
        </div>

        {/* Statut officiel */}
        <div className="mt-5 bg-white border border-gray-200 rounded-xl p-5 shadow">
          <h2 className="font-heading text-sm font-bold text-gray-900 mb-3">📜 Statut officiel</h2>
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Récépissé MATDDT (Togo)</span>
              {s.matddt_recepisse?.number ? (
                <span className="font-mono font-bold text-emerald-700" data-testid="verify-matddt">
                  {s.matddt_recepisse.number}
                </span>
              ) : (
                <span className="text-xs text-amber-700 italic" data-testid="verify-matddt-pending">
                  Dossier N° 0564 déposé 05/08/2026 — récépissé en cours
                </span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">NIF Fiscal (OTR)</span>
              {s.togo_nif?.number ? (
                <span className="font-mono font-bold text-amber-700">{s.togo_nif.number}</span>
              ) : (
                <span className="text-gray-400 italic">non attribué</span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Charity Commission (UK)</span>
              <span className={`inline-block text-white text-[10px] font-bold px-2 py-1 rounded-full ${cs.color}`}>
                {cs.label}{s.charity_uk?.charity_number ? ` — n° ${s.charity_uk.charity_number}` : ""}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-5 text-center text-[11px] text-gray-500">
          Cette page est délivrée automatiquement par le serveur SAFUAYE et sert de <b>preuve d'authenticité</b> du PDF.
          Toute modification du document invalide immédiatement l'empreinte SHA-256 affichée ci-dessus.
        </div>
      </div>
    </div>
  );
}
